import { useRef, useState } from "react";
import { Camera, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { destinoSubida } from "@/lib/subidas";

function fileToBase64(file, maxSize = 1280, quality = 0.78) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          } else {
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function PhotoUploader({
  label,
  value,
  onChange,
  multiple = false,
  testid,
  uploadCode = null, // reservation code → store on server, keep URLs in Mongo
  adminUpload = false, // subida del panel (con sesión): sin reservationId, pública (vehículos, productos)
  reservationId = null, // con adminUpload: foto privada de esa reserva
  onRaw = null, // callback with the resized data-URL (e.g. OCR) before upload
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Upload to local object storage; fall back to inline base64 so the flow
  // never blocks on a storage hiccup (backend accepts both formats).
  const toStored = async (b64) => {
    const endpoint = destinoSubida({ uploadCode, adminUpload, reservationId });
    if (!endpoint) return b64;
    try {
      const { data } = await api.post(endpoint, { image: b64 });
      return data.url || b64;
    } catch {
      return b64;
    }
  };

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      if (multiple) {
        const raws = await Promise.all([...files].map((f) => fileToBase64(f)));
        if (onRaw) raws.forEach((r) => onRaw(r));
        const stored = await Promise.all(raws.map(toStored));
        onChange([...(value || []), ...stored]);
      } else {
        const b64 = await fileToBase64(files[0]);
        if (onRaw) onRaw(b64);
        onChange(await toStored(b64));
      }
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  if (!multiple) {
    return (
      <div data-testid={testid}>
        {label && (
          <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
            {label}
          </label>
        )}
        {value ? (
          <div className="relative rounded-xl overflow-hidden border-2 border-[#DCD8D0] bg-white">
            <img src={value} alt={label} className="w-full max-h-64 object-contain" />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow hover:bg-white"
              data-testid={`${testid}-remove`}
            >
              <X className="h-4 w-4 text-[#1A1A1A]" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full border-2 border-dashed border-[#DCD8D0] bg-[#F7F5F0] rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer hover:border-marca transition-colors duration-200"
            data-testid={`${testid}-trigger`}
          >
            {uploading ? (
              <Loader2 className="h-8 w-8 text-[#5C5C5C] mb-2 animate-spin" strokeWidth={1.5} />
            ) : (
              <Camera className="h-8 w-8 text-[#5C5C5C] mb-2" strokeWidth={1.5} />
            )}
            <span className="text-sm text-[#5C5C5C]">{uploading ? "Subiendo..." : "Toca para añadir foto"}</span>
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
    );
  }

  return (
    <div data-testid={testid}>
      {label && (
        <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
          {label}
        </label>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {(value || []).map((src, idx) => (
          <div
            key={idx}
            className="relative aspect-square rounded-xl overflow-hidden border border-[#DCD8D0] bg-white"
          >
            <img src={src} alt={`foto-${idx}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removeAt(idx)}
              className="absolute top-1 right-1 bg-white/90 rounded-full p-1 shadow hover:bg-white"
              data-testid={`${testid}-remove-${idx}`}
            >
              <X className="h-3.5 w-3.5 text-[#1A1A1A]" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="aspect-square border-2 border-dashed border-[#DCD8D0] bg-[#F7F5F0] rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-marca transition-colors duration-200"
          data-testid={`${testid}-trigger`}
        >
          {uploading ? (
            <Loader2 className="h-7 w-7 text-[#5C5C5C] animate-spin" strokeWidth={1.5} />
          ) : (
            <Camera className="h-7 w-7 text-[#5C5C5C]" strokeWidth={1.5} />
          )}
          <span className="text-xs text-[#5C5C5C] mt-1">{uploading ? "Subiendo..." : "Añadir"}</span>
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}
